import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Sparkles,
  Printer,
  Mail,
  Zap,
  Target,
  BookOpen,
  Award,
  AlertTriangle,
  CheckCircle2,
  Clock,
  TrendingUp,
  BrainCircuit,
  MessageSquare,
  ArrowUpRight,
  ShieldAlert,
  Loader2,
  Send,
} from "lucide-react";
import { client } from "@/src/api/client.gen";
import {
  MemberDetailResponse,
  MemberCompetencyDetailItem,
  MemberCourseProgressItem,
  getErrorMessage,
} from "../types";
import { CompetencyRadarChart } from "./CompetencyRadarChart";

interface EmployeeDossierDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  orgSlug: string;
  userId: number | null;
  memberName?: string | null;
  memberEmail?: string | null;
}

export const EmployeeDossierDrawer: React.FC<EmployeeDossierDrawerProps> = ({
  isOpen,
  onClose,
  orgSlug,
  userId,
  memberName,
  memberEmail,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MemberDetailResponse | null>(null);
  const [activeTab, setActiveTab] = useState<"briefing" | "competencies" | "courses" | "quizzes">("briefing");

  // Action states
  const [nudging, setNudging] = useState(false);
  const [nudgeSuccess, setNudgeSuccess] = useState<string | null>(null);
  const [customNudgeMsg, setCustomNudgeMsg] = useState("");
  const [showNudgeInput, setShowNudgeInput] = useState(false);

  const [remediating, setRemediating] = useState(false);
  const [remediationSuccess, setRemediationSuccess] = useState<string | null>(null);

  const fetchMemberDetail = useCallback(async () => {
    if (!userId || !orgSlug) return;
    setLoading(true);
    setError(null);
    try {
      const res: any = await client.get({
        url: `/api/v1/b2b/${orgSlug}/members/${userId}/detail`,
      });
      if (res && res.data) {
        setData(res.data as MemberDetailResponse);
      } else if (res) {
        setData(res as MemberDetailResponse);
      }
    } catch (err: any) {
      console.error("Failed to load employee dossier:", err);
      setError(getErrorMessage(err, "Failed to load employee intelligence details."));
    } finally {
      setLoading(false);
    }
  }, [orgSlug, userId]);

  useEffect(() => {
    if (isOpen && userId) {
      setActiveTab("briefing");
      setNudgeSuccess(null);
      setRemediationSuccess(null);
      setShowNudgeInput(false);
      fetchMemberDetail();
    } else {
      setData(null);
    }
  }, [isOpen, userId, fetchMemberDetail]);

  const handleSendNudge = async () => {
    if (!userId || !orgSlug) return;
    setNudging(true);
    setNudgeSuccess(null);
    try {
      const res: any = await client.post({
        url: `/api/v1/b2b/${orgSlug}/members/${userId}/nudge`,
        body: customNudgeMsg.trim() ? { custom_message: customNudgeMsg.trim() } : {},
      });
      const msg = res?.data?.message || res?.message || "Reminder nudge sent to employee successfully.";
      setNudgeSuccess(msg);
      setShowNudgeInput(false);
      setCustomNudgeMsg("");
    } catch (err: any) {
      alert(getErrorMessage(err, "Failed to send nudge."));
    } finally {
      setNudging(false);
    }
  };

  const handleTriggerRemediation = async (skillName: string, weakConcept: string) => {
    if (!userId || !orgSlug) return;
    setRemediating(true);
    setRemediationSuccess(null);
    try {
      const res: any = await client.post({
        url: `/api/v1/b2b/${orgSlug}/members/${userId}/remediation/trigger`,
        body: {
          skill_name: skillName,
          weak_concept: weakConcept,
          notes: `Triggered by manager via 1-on-1 dossier drawer`,
        },
      });
      const msg = res?.data?.message || res?.message || `AI remediation micro-lesson queued for ${weakConcept}.`;
      setRemediationSuccess(msg);
      // Refresh dossier after triggering
      await fetchMemberDetail();
    } catch (err: any) {
      alert(getErrorMessage(err, "Failed to trigger remediation."));
    } finally {
      setRemediating(false);
    }
  };

  const handlePrintDossier = () => {
    window.print();
  };

  if (!isOpen) return null;

  const statusColors: Record<string, { bg: string; text: string; label: string }> = {
    top_performer: { bg: "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800", text: "text-emerald-700 dark:text-emerald-300", label: "Top Performer" },
    on_track: { bg: "bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800", text: "text-blue-700 dark:text-blue-300", label: "On Track" },
    gaps_detected: { bg: "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800", text: "text-amber-700 dark:text-amber-300", label: "Gaps Detected" },
    overdue: { bg: "bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800", text: "text-rose-700 dark:text-rose-300", label: "At Risk / Overdue" },
    approaching_deadline: { bg: "bg-yellow-50 dark:bg-yellow-950/40 border-yellow-200 dark:border-yellow-800", text: "text-yellow-700 dark:text-yellow-300", label: "Deadline Nearing" },
  };

  const statusConfig = data ? (statusColors[data.overall_status] || statusColors.on_track) : statusColors.on_track;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden print:p-0">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity print:hidden"
        onClick={onClose}
      />

      <div className="fixed inset-y-0 right-0 max-w-full flex pl-10 print:static print:p-0 print:w-full print:max-w-none">
        <div className="w-screen max-w-3xl bg-white dark:bg-neutral-900 shadow-2xl flex flex-col border-l border-slate-200 dark:border-neutral-800 print:border-none print:shadow-none print:w-full print:max-w-none">
          {/* Header */}
          <div className="p-6 border-b border-slate-200 dark:border-neutral-800 flex items-start justify-between bg-slate-50/70 dark:bg-neutral-950/40 print:bg-white print:border-b-2">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-violet-600 to-indigo-500 flex items-center justify-center text-white text-xl font-bold shadow-md shadow-violet-500/20">
                {(data?.name || memberName || "U").charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="flex items-center space-x-3">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                    {data?.name || memberName || "Employee Intelligence"}
                  </h2>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusConfig.bg} ${statusConfig.text}`}
                  >
                    {statusConfig.label}
                  </span>
                </div>
                <p className="text-xs text-slate-500 dark:text-neutral-400 mt-0.5">
                  {data?.job_title ? `${data.job_title} • ` : ""}
                  {data?.department ? `${data.department} • ` : ""}
                  {data?.email || memberEmail}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2 print:hidden">
              <button
                type="button"
                onClick={handlePrintDossier}
                className="inline-flex items-center space-x-1 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-xs font-semibold text-slate-700 dark:text-neutral-200 hover:bg-slate-50 dark:hover:bg-neutral-700 transition"
                title="Print or save PDF Dossier"
              >
                <Printer className="w-3.5 h-3.5" />
                <span>Print Dossier</span>
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-neutral-200 hover:bg-slate-100 dark:hover:bg-neutral-800 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Quick Metrics Strip */}
          {data && (
            <div className="grid grid-cols-4 divide-x divide-slate-100 dark:divide-neutral-800 border-b border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-center py-3">
              <div className="px-3">
                <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider block">Courses</span>
                <span className="text-sm font-bold text-slate-900 dark:text-white">
                  {data.total_courses_completed}/{data.total_courses_enrolled}
                </span>
                <span className="text-[10px] text-slate-500 block">Completed</span>
              </div>
              <div className="px-3">
                <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider block">Avg Quiz</span>
                <span className="text-sm font-bold text-violet-600 dark:text-violet-400">
                  {Math.round(data.overall_avg_quiz_score)}%
                </span>
                <span className="text-[10px] text-slate-500 block">All Modules</span>
              </div>
              <div className="px-3">
                <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider block">Skill Gaps</span>
                <span className={`text-sm font-bold ${data.weak_concepts.length > 0 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                  {data.weak_concepts.length}
                </span>
                <span className="text-[10px] text-slate-500 block">Identified</span>
              </div>
              <div className="px-3">
                <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider block">Dept Rank</span>
                <span className="text-sm font-bold text-slate-900 dark:text-white">
                  {data.peer_percentiles.department_rank}
                </span>
                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 block font-medium">
                  {data.peer_percentiles.quiz_performance_percentile}th %ile
                </span>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex border-b border-slate-200 dark:border-neutral-800 px-6 pt-2 bg-slate-50/40 dark:bg-neutral-950/20 print:hidden">
            <button
              type="button"
              onClick={() => setActiveTab("briefing")}
              className={`flex items-center space-x-2 py-3 px-3 text-xs font-semibold border-b-2 transition ${
                activeTab === "briefing"
                  ? "border-violet-600 text-violet-600 dark:text-violet-400"
                  : "border-transparent text-slate-500 dark:text-neutral-400 hover:text-slate-800 dark:hover:text-neutral-200"
              }`}
            >
              <BrainCircuit className="w-4 h-4" />
              <span>AI Manager 1-on-1 Briefing</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("competencies")}
              className={`flex items-center space-x-2 py-3 px-3 text-xs font-semibold border-b-2 transition ${
                activeTab === "competencies"
                  ? "border-violet-600 text-violet-600 dark:text-violet-400"
                  : "border-transparent text-slate-500 dark:text-neutral-400 hover:text-slate-800 dark:hover:text-neutral-200"
              }`}
            >
              <Target className="w-4 h-4" />
              <span>Competency Radar ({data?.competencies.length || 0})</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("courses")}
              className={`flex items-center space-x-2 py-3 px-3 text-xs font-semibold border-b-2 transition ${
                activeTab === "courses"
                  ? "border-violet-600 text-violet-600 dark:text-violet-400"
                  : "border-transparent text-slate-500 dark:text-neutral-400 hover:text-slate-800 dark:hover:text-neutral-200"
              }`}
            >
              <BookOpen className="w-4 h-4" />
              <span>Enrolled Courses ({data?.courses.length || 0})</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("quizzes")}
              className={`flex items-center space-x-2 py-3 px-3 text-xs font-semibold border-b-2 transition ${
                activeTab === "quizzes"
                  ? "border-violet-600 text-violet-600 dark:text-violet-400"
                  : "border-transparent text-slate-500 dark:text-neutral-400 hover:text-slate-800 dark:hover:text-neutral-200"
              }`}
            >
              <Award className="w-4 h-4" />
              <span>Quiz History & Remediation</span>
            </button>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {loading ? (
              <div className="py-20 flex flex-col items-center justify-center space-y-3">
                <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
                <p className="text-xs text-slate-500">Synthesizing employee learning intelligence...</p>
              </div>
            ) : error ? (
              <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 text-xs">
                {error}
              </div>
            ) : !data ? null : (
              <>
                {/* Status Banners / Notifications */}
                {nudgeSuccess && (
                  <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl text-emerald-800 dark:text-emerald-300 text-xs flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span>{nudgeSuccess}</span>
                    </div>
                    <button type="button" onClick={() => setNudgeSuccess(null)} className="text-emerald-600 hover:text-emerald-800 text-xs">
                      Dismiss
                    </button>
                  </div>
                )}
                {remediationSuccess && (
                  <div className="p-3 bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 rounded-xl text-violet-800 dark:text-violet-300 text-xs flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Sparkles className="w-4 h-4 text-violet-600 flex-shrink-0" />
                      <span>{remediationSuccess}</span>
                    </div>
                    <button type="button" onClick={() => setRemediationSuccess(null)} className="text-violet-600 hover:text-violet-800 text-xs">
                      Dismiss
                    </button>
                  </div>
                )}

                {/* TAB 1: AI MANAGER 1-ON-1 BRIEFING */}
                {(activeTab === "briefing" || window.matchMedia("print").matches) && (
                  <div className="space-y-6">
                    {/* Executive Summary Card */}
                    <div className="p-5 rounded-2xl bg-gradient-to-br from-violet-500/10 via-indigo-500/5 to-purple-500/10 border border-violet-200/80 dark:border-violet-900/50 relative overflow-hidden">
                      <div className="flex items-center space-x-2 text-violet-700 dark:text-violet-300 font-semibold text-xs mb-2">
                        <Sparkles className="w-4 h-4 text-violet-600 animate-pulse" />
                        <span>AI Executive Summary</span>
                      </div>
                      <p className="text-sm text-slate-800 dark:text-neutral-200 leading-relaxed font-medium">
                        {data.ai_manager_briefing.executive_summary}
                      </p>
                    </div>

                    {/* Key Strengths & Blockers Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Key Strengths */}
                      <div className="p-4 rounded-xl bg-slate-50 dark:bg-neutral-900/50 border border-slate-200 dark:border-neutral-800">
                        <div className="flex items-center space-x-2 text-emerald-600 dark:text-emerald-400 font-semibold text-xs mb-3">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Demonstrated Strengths</span>
                        </div>
                        {data.ai_manager_briefing.key_strengths.length === 0 ? (
                          <p className="text-xs text-slate-400">Consistent baseline performance across all modules.</p>
                        ) : (
                          <ul className="space-y-2">
                            {data.ai_manager_briefing.key_strengths.map((str, idx) => (
                              <li key={idx} className="text-xs text-slate-700 dark:text-neutral-300 flex items-start space-x-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                                <span>{str}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      {/* Active Blockers */}
                      <div className="p-4 rounded-xl bg-slate-50 dark:bg-neutral-900/50 border border-slate-200 dark:border-neutral-800">
                        <div className="flex items-center space-x-2 text-amber-600 dark:text-amber-400 font-semibold text-xs mb-3">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Active Learning Blockers</span>
                        </div>
                        {data.ai_manager_briefing.active_blockers.length === 0 ? (
                          <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">No critical blockers identified. Progressing smoothly.</p>
                        ) : (
                          <ul className="space-y-2">
                            {data.ai_manager_briefing.active_blockers.map((blk, idx) => (
                              <li key={idx} className="text-xs text-slate-700 dark:text-neutral-300 flex items-start space-x-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                                <span>{blk}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    {/* Suggested 1-on-1 Review Agenda Points */}
                    <div className="p-5 rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 shadow-sm space-y-3">
                      <div className="flex items-center space-x-2 text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                        <MessageSquare className="w-4 h-4 text-violet-600" />
                        <span>Recommended 1-on-1 Coaching Talking Points</span>
                      </div>
                      <div className="space-y-2.5">
                        {data.ai_manager_briefing.suggested_1on1_talking_points.map((tp, idx) => (
                          <div
                            key={idx}
                            className="p-3 rounded-xl bg-slate-50 dark:bg-neutral-950/40 border border-slate-200/80 dark:border-neutral-800 flex items-start space-x-3 text-xs text-slate-800 dark:text-neutral-200"
                          >
                            <span className="w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-950 text-violet-600 dark:text-violet-400 font-bold flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">
                              {idx + 1}
                            </span>
                            <span className="leading-relaxed font-medium">{tp}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Manager One-Click Interventions */}
                    <div className="p-5 rounded-2xl bg-slate-50 dark:bg-neutral-950/50 border border-slate-200 dark:border-neutral-800 space-y-3 print:hidden">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Zap className="w-4 h-4 text-violet-600" />
                          <h4 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                            Direct Manager Interventions
                          </h4>
                        </div>
                        <span className="text-[11px] text-slate-400">One-click actions</span>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => setShowNudgeInput(!showNudgeInput)}
                          className="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-700 text-xs font-semibold text-slate-700 dark:text-neutral-200 hover:border-violet-400 hover:text-violet-600 dark:hover:text-violet-400 transition shadow-sm"
                        >
                          <Mail className="w-3.5 h-3.5 text-violet-500" />
                          <span>{showNudgeInput ? "Cancel Nudge" : "Send Manager Nudge Email"}</span>
                        </button>

                        {data.weak_concepts.length > 0 && (
                          <button
                            type="button"
                            disabled={remediating}
                            onClick={() => handleTriggerRemediation(data.competencies[0]?.skill_name || "General Competency", data.weak_concepts[0])}
                            className="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold transition shadow-sm disabled:opacity-50"
                          >
                            {remediating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BrainCircuit className="w-3.5 h-3.5" />}
                            <span>Queue AI Remediation ({data.weak_concepts[0]})</span>
                          </button>
                        )}
                      </div>

                      {showNudgeInput && (
                        <div className="mt-3 p-3 bg-white dark:bg-neutral-900 rounded-xl border border-violet-200 dark:border-neutral-700 space-y-2">
                          <label className="text-[11px] font-medium text-slate-500 block">
                            Custom encouraging note (optional, default reminder sent if empty):
                          </label>
                          <textarea
                            value={customNudgeMsg}
                            onChange={(e) => setCustomNudgeMsg(e.target.value)}
                            rows={2}
                            placeholder="e.g., Hey Alex, noticed you're almost done with Module 3. Let's review it in our 1-on-1 this Thursday!"
                            className="w-full text-xs p-2.5 rounded-lg border border-slate-200 dark:border-neutral-700 bg-slate-50 dark:bg-neutral-800 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                          />
                          <div className="flex justify-end space-x-2">
                            <button
                              type="button"
                              onClick={() => setShowNudgeInput(false)}
                              className="px-3 py-1 text-xs text-slate-500 hover:text-slate-700"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              disabled={nudging}
                              onClick={handleSendNudge}
                              className="inline-flex items-center space-x-1.5 px-3.5 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition"
                            >
                              {nudging ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                              <span>Send Nudge Now</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* TAB 2: COMPETENCIES & RADAR CHART */}
                {activeTab === "competencies" && (
                  <div className="space-y-6">
                    {/* Radar Chart Card */}
                    <div className="p-6 rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 shadow-sm flex flex-col items-center">
                      <div className="w-full flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center space-x-2">
                            <Target className="w-4 h-4 text-violet-600" />
                            <span>Competency Radar: Verified Skill vs. Target Expectation</span>
                          </h3>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Visual evaluation across mapped workspace job expectations
                          </p>
                        </div>
                      </div>

                      <CompetencyRadarChart competencies={data.competencies} size={340} />
                    </div>

                    {/* Competency Roster List */}
                    <div className="space-y-3">
                      <h4 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                        Evaluated Skill Breakdown ({data.competencies.length})
                      </h4>
                      {data.competencies.length === 0 ? (
                        <p className="text-xs text-slate-400">No skill competencies recorded yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {data.competencies.map((comp) => {
                            const isMastered = comp.status === "mastered" || comp.diagnosed_score >= 80;
                            return (
                              <div
                                key={comp.id}
                                className="p-4 rounded-xl bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4"
                              >
                                <div className="space-y-1.5 flex-1">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-semibold text-slate-900 dark:text-white">
                                      {comp.skill_name}
                                    </span>
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                                        isMastered
                                          ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                                          : "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                                      }`}
                                    >
                                      {comp.status.replace("_", " ")}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-4 text-xs text-slate-500 dark:text-neutral-400">
                                    <span>Current: <strong className="text-slate-800 dark:text-neutral-200">{comp.current_proficiency}</strong></span>
                                    <span>Target: <strong className="text-slate-800 dark:text-neutral-200">{comp.target_proficiency}</strong></span>
                                    <span>Score: <strong className="text-violet-600">{comp.diagnosed_score}%</strong></span>
                                  </div>

                                  {comp.weak_concepts.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                      {comp.weak_concepts.map((concept, cIdx) => (
                                        <span
                                          key={cIdx}
                                          className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 text-[11px] border border-amber-200 dark:border-amber-800/60"
                                        >
                                          <AlertTriangle className="w-2.5 h-2.5" />
                                          <span>{concept}</span>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                <div className="flex items-center space-x-2 flex-shrink-0">
                                  {comp.weak_concepts.length > 0 && (
                                    <button
                                      type="button"
                                      disabled={remediating}
                                      onClick={() => handleTriggerRemediation(comp.skill_name, comp.weak_concepts[0])}
                                      className="px-3 py-1.5 rounded-lg bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-800 hover:bg-violet-100 text-xs font-semibold transition"
                                    >
                                      Assign Remediation
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* TAB 3: ENROLLED COURSES */}
                {activeTab === "courses" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                        Assigned Learning Path ({data.courses.length})
                      </h4>
                      <span className="text-xs text-slate-400">
                        {data.total_courses_completed} completed of {data.total_courses_enrolled} enrolled
                      </span>
                    </div>

                    {data.courses.length === 0 ? (
                      <p className="text-xs text-slate-400">No courses assigned to this employee yet.</p>
                    ) : (
                      <div className="space-y-3">
                        {data.courses.map((course) => {
                          const isDone = course.progress_percent >= 100;
                          return (
                            <div
                              key={course.course_id}
                              className="p-4 rounded-xl bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 shadow-sm space-y-3"
                            >
                              <div className="flex items-start justify-between">
                                <div className="space-y-1">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                                      {course.course_title}
                                    </span>
                                    {course.is_mandatory && (
                                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300 uppercase">
                                        Mandatory
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-500">
                                    Current: <span className="font-medium text-slate-700 dark:text-neutral-300">{course.current_module_title || "Not started"}</span>
                                  </p>
                                </div>

                                <div className="text-right">
                                  <span className="text-sm font-bold text-violet-600 dark:text-violet-400">
                                    {course.progress_percent}%
                                  </span>
                                  <span className="text-[11px] text-slate-400 block">
                                    {course.completed_modules}/{course.total_modules} modules
                                  </span>
                                </div>
                              </div>

                              {/* Progress bar */}
                              <div className="h-2 w-full bg-slate-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-500 ${
                                    isDone ? "bg-emerald-500" : "bg-violet-600"
                                  }`}
                                  style={{ width: `${course.progress_percent}%` }}
                                />
                              </div>

                              <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1">
                                <div className="flex items-center space-x-1.5">
                                  <Clock className="w-3 h-3" />
                                  <span>
                                    {course.due_date ? `Deadline: ${course.due_date}` : "Self-paced"}
                                  </span>
                                </div>
                                <span className={`font-semibold capitalize ${
                                  course.status === "completed"
                                    ? "text-emerald-600"
                                    : course.status === "overdue"
                                    ? "text-rose-600"
                                    : "text-blue-600"
                                }`}>
                                  {course.status.replace("_", " ")}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* TAB 4: QUIZ HISTORY & REMEDIATION */}
                {activeTab === "quizzes" && (
                  <div className="space-y-6">
                    {/* Active Remediation Items */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <BrainCircuit className="w-4 h-4 text-violet-600" />
                        <h4 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                          Active AI Remediation Micro-Lessons ({data.active_remediations.length})
                        </h4>
                      </div>

                      {data.active_remediations.length === 0 ? (
                        <div className="p-4 rounded-xl bg-slate-50 dark:bg-neutral-900/40 border border-slate-200 dark:border-neutral-800 text-center text-xs text-slate-400">
                          No active remediation tasks. All module concepts verified or passed.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {data.active_remediations.map((rem) => (
                            <div
                              key={rem.id}
                              className="p-3.5 rounded-xl bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 flex items-center justify-between"
                            >
                              <div className="space-y-0.5">
                                <span className="text-xs font-bold text-slate-900 dark:text-white">
                                  {rem.weak_concept}
                                </span>
                                <span className="text-[11px] text-slate-400 block">
                                  Skill Area: {rem.skill_name}
                                </span>
                              </div>
                              <span
                                className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${
                                  rem.is_resolved
                                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                                    : "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300 animate-pulse"
                                }`}
                              >
                                {rem.is_resolved ? "Resolved" : "Pending Learner Review"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Recent Quiz Attempts */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Award className="w-4 h-4 text-violet-600" />
                        <h4 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                          Recent Module Quiz Attempts ({data.recent_quizzes.length})
                        </h4>
                      </div>

                      {data.recent_quizzes.length === 0 ? (
                        <div className="p-4 rounded-xl bg-slate-50 dark:bg-neutral-900/40 border border-slate-200 dark:border-neutral-800 text-center text-xs text-slate-400">
                          No quiz attempts submitted yet.
                        </div>
                      ) : (
                        <div className="overflow-hidden border border-slate-200 dark:border-neutral-800 rounded-xl">
                          <table className="min-w-full divide-y divide-slate-200 dark:divide-neutral-800 text-left text-xs">
                            <thead className="bg-slate-50 dark:bg-neutral-950 text-slate-500 font-semibold">
                              <tr>
                                <th className="px-4 py-2.5">Module</th>
                                <th className="px-4 py-2.5">Attempt</th>
                                <th className="px-4 py-2.5">Score</th>
                                <th className="px-4 py-2.5">Result</th>
                                <th className="px-4 py-2.5 text-right">Date</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-neutral-800/70 bg-white dark:bg-neutral-900">
                              {data.recent_quizzes.map((quiz) => (
                                <tr key={quiz.id}>
                                  <td className="px-4 py-3 font-medium text-slate-800 dark:text-neutral-200">
                                    {quiz.module_title}
                                  </td>
                                  <td className="px-4 py-3 text-slate-500">
                                    #{quiz.attempt_number}
                                  </td>
                                  <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                                    {Math.round(quiz.score)}%
                                  </td>
                                  <td className="px-4 py-3">
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                        quiz.passed
                                          ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                                          : "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
                                      }`}
                                    >
                                      {quiz.passed ? "Passed" : "Retake Needed"}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-right text-slate-400 text-[11px]">
                                    {quiz.submitted_at ? new Date(quiz.submitted_at).toLocaleDateString() : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
